import React, { useState, useEffect, useCallback } from "react"
import {
  Search, ScrollText, Building2, Shield, UserCog, Home, Mail, CheckCircle2,
  ArrowUpDown, ArrowUp, ArrowDown, ChevronRight
} from "lucide-react"
import { Input } from "src/components/ui/input"
import { Skeleton } from "src/components/ui/skeleton"
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "src/components/ui/table"
import { useAuth } from "src/contexts/AuthContext"
import { NoAccess } from "src/components/shared/NoAccess"
import { useAuditLogs } from "src/features/members/hooks/useAuditLogs"

// Maps audit action keys to friendly text + an icon (the "i18n translation"
// step). Falls back to a humanized version of the key for unmapped actions.
const ACTION_META = {
  "project.created":           { label: "Created a project", icon: Building2 },
  "project.destroyed":         { label: "Deleted a project", icon: Building2 },
  "unit.status_changed":       { label: "Changed a unit's status", icon: Home },
  "role.permissions_updated":  { label: "Updated role permissions", icon: Shield },
  "member.role_changed":       { label: "Changed a member's role", icon: UserCog },
  "invitation.created":        { label: "Sent an invitation", icon: Mail },
  "invitation.accepted":      { label: "Accepted an invitation", icon: CheckCircle2 },
}

function humanizeAction(action) {
  return action.replace(/[._]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
}

function describe(log) {
  const m = ACTION_META[log.action]
  return { label: m?.label || humanizeAction(log.action), Icon: m?.icon || ScrollText }
}

// Short context line from metadata so a row reads on its own.
function detailLine(log) {
  const d = log.metadata || {}
  switch (log.action) {
    case "unit.status_changed":
      return [d.unit_number, d.from && d.to && `${d.from} → ${d.to}`, d.reason && `“${d.reason}”`].filter(Boolean).join(" · ")
    case "member.role_changed":
      return [d.member_email, d.from && d.to && `${d.from} → ${d.to}`].filter(Boolean).join(" · ")
    case "role.permissions_updated":
      return d.role
    case "invitation.created":
    case "invitation.accepted":
      return [d.email, d.role].filter(Boolean).join(" · ")
    case "project.created":
    case "project.destroyed":
      return d.name
    default:
      return ""
  }
}

// Em-dash placeholder shown wherever a value is missing/empty.
const EMPTY = "—"

function isEmpty(v) {
  return v == null || v === ""
}

function titleize(str) {
  if (isEmpty(str)) return ""
  return String(str).replace(/[._]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
}

// A single labelled fact shown in the expanded detail panel.
// Renders an em-dash when the value is null/empty rather than hiding the field.
function Field({ label, children }) {
  const empty = isEmpty(children)
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{label}</span>
      <span className={`text-[13px] ${empty ? "text-slate-400" : "text-slate-700"}`}>
        {empty ? EMPTY : children}
      </span>
    </div>
  )
}

function FromTo({ from, to }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[12px] text-slate-600">{isEmpty(from) ? EMPTY : from}</span>
      <ArrowRight />
      <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[12px] font-medium text-emerald-700">{isEmpty(to) ? EMPTY : to}</span>
    </span>
  )
}

function ArrowRight() {
  return <span className="text-slate-400">→</span>
}

// A single Read/Manage cell in the permissions-change table. When the value
// changed, shows "old → new" with colour; otherwise just the current state.
function PermCell({ before, after }) {
  const mark = (v) => (v ? "✓" : "—")
  if (before === undefined) {
    // No before-snapshot (older logs): just show the resulting state.
    return <span className="text-center text-slate-600">{mark(after)}</span>
  }
  if (!!before === !!after) {
    return <span className="text-center text-slate-400">{mark(after)}</span>
  }
  const granted = !before && after
  return (
    <span className={`text-center font-medium ${granted ? "text-emerald-600" : "text-red-500"}`}>
      {mark(before)} → {mark(after)}
    </span>
  )
}

// Renders the before→after permission diff for role.permissions_updated.
// Falls back to the resulting snapshot when no before-state was recorded.
function PermissionsChange({ before, after }) {
  const a = after || {}
  const b = before // may be undefined for older logs
  const resources = Array.from(new Set([...Object.keys(b || {}), ...Object.keys(a)]))

  const changed = b
    ? resources.filter((r) => {
        const bv = b[r] || {}
        const av = a[r] || {}
        return !!bv.read !== !!av.read || !!bv.manage !== !!av.manage
      })
    : resources

  const hasDiff = b != null
  const list = hasDiff ? changed : resources

  return (
    <div>
      <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
        {hasDiff ? "Changes" : "Permissions"}
      </span>
      <div className="mt-1.5 overflow-hidden rounded-lg border border-slate-200">
        <div className="grid grid-cols-[1fr_90px_90px] bg-slate-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          <span>Resource</span>
          <span className="text-center">Read</span>
          <span className="text-center">Manage</span>
        </div>
        {list.length === 0 ? (
          <p className="px-3 py-2 text-[12px] text-slate-400">
            {hasDiff ? "No permission changes." : "No permissions recorded."}
          </p>
        ) : (
          list.map((res, i) => {
            const bv = (b || {})[res] || {}
            const av = a[res] || {}
            return (
              <div
                key={res}
                className={`grid grid-cols-[1fr_90px_90px] items-center px-3 py-1.5 text-[13px] ${i > 0 ? "border-t border-slate-100" : ""}`}
              >
                <span className="text-slate-700">{titleize(res)}</span>
                <PermCell before={hasDiff ? bv.read : undefined} after={av.read} />
                <PermCell before={hasDiff ? bv.manage : undefined} after={av.manage} />
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

// Renders the audit metadata in a human-readable layout, per action type.
function AuditDetails({ log }) {
  const d = log.metadata || {}

  if (log.action === "role.permissions_updated") {
    return (
      <div className="space-y-3">
        <Field label="Role">{titleize(d.role || "")}</Field>
        <PermissionsChange before={d.before} after={d.after || d.permissions} />
      </div>
    )
  }

  if (log.action === "member.role_changed") {
    return (
      <div className="flex flex-wrap gap-x-10 gap-y-3">
        <Field label="Member">{d.member_email}</Field>
        <Field label="Role change"><FromTo from={titleize(d.from)} to={titleize(d.to)} /></Field>
      </div>
    )
  }

  if (log.action === "unit.status_changed") {
    return (
      <div className="flex flex-wrap gap-x-10 gap-y-3">
        <Field label="Unit">{d.unit_number}</Field>
        <Field label="Status change"><FromTo from={titleize(d.from)} to={titleize(d.to)} /></Field>
        <Field label="Reason">{isEmpty(d.reason) ? null : `“${d.reason}”`}</Field>
      </div>
    )
  }

  if (log.action === "invitation.accepted") {
    return (
      <div className="flex flex-wrap gap-x-10 gap-y-3">
        <Field label="Email">{d.email}</Field>
        <Field label="Role">{titleize(d.role)}</Field>
      </div>
    )
  }

  if (log.action === "project.destroyed") {
    return (
      <div className="flex flex-wrap gap-x-10 gap-y-3">
        <Field label="Project">{d.name}</Field>
      </div>
    )
  }

  // Generic fallback: list each metadata key/value, plus the entity reference.
  const keys = Object.keys(d)
  return (
    <div className="flex flex-wrap gap-x-10 gap-y-3">
      {keys.length === 0 && !log.entity_type ? (
        <p className="text-[13px] text-slate-400">No additional details recorded.</p>
      ) : (
        <>
          {keys.map((k) => (
            <Field key={k} label={titleize(k)}>
              {isEmpty(d[k]) ? null : (typeof d[k] === "object" ? JSON.stringify(d[k]) : String(d[k]))}
            </Field>
          ))}
          {log.entity_type && (
            <Field label="Entity">{titleize(log.entity_type)}{log.entity_id ? ` #${log.entity_id}` : ""}</Field>
          )}
        </>
      )}
    </div>
  )
}

// Creation events have no "what changed" detail, so their rows don't expand.
function isExpandable(log) {
  return !/\.created$/.test(log.action)
}

function relativeTime(iso) {
  if (!iso) return ""
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)
  if (mins < 1) return "just now"
  if (mins < 60) return `${mins}m ago`
  if (hours < 24) return `${hours}h ago`
  if (days < 30) return `${days}d ago`
  return new Date(iso).toLocaleDateString()
}

function useDebounce(value, delay = 300) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

function initials(name = "") {
  return name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("") || "?"
}

// Privileged roles that may read the audit trail (mirrors AuditLogPolicy).
const AUDIT_VIEWER_ROLES = ["org_admin", "site_head", "team_lead"]

export default function AuditLogsPage() {
  const { currentMembership } = useAuth()
  const canView = AUDIT_VIEWER_ROLES.includes(currentMembership?.role?.slug)

  const [searchRaw, setSearch] = useState("")
  const q = useDebounce(searchRaw)
  const [page, setPage] = useState(1)

  const { data, loading, fetch } = useAuditLogs()

  const [sortField, setSortField] = useState(null)
  const [sortDirection, setSortDirection] = useState(null)
  const [expandedId, setExpandedId] = useState(null)

  // Toggle a row open/closed — but don't treat a text-selection drag as a click.
  const toggleRow = (id) => {
    if (window.getSelection?.().toString()) return
    setExpandedId((cur) => (cur === id ? null : id))
  }

  const handleSort = (field) => {
    if (sortField === field) {
      if (sortDirection === "asc") setSortDirection("desc")
      else if (sortDirection === "desc") {
        setSortField(null)
        setSortDirection(null)
      }
    } else {
      setSortField(field)
      setSortDirection("asc")
    }
  }

  useEffect(() => { if (canView) fetch({ page, per_page: 25, q }) }, [page, q, canView, fetch])
  useEffect(() => { setPage(1) }, [q])

  if (!canView) {
    return <NoAccess message="You don't have permission to view the audit log." />
  }

  const logs = data?.audit_logs ?? []

  const sortedLogs = React.useMemo(() => {
    if (!sortField || !sortDirection) return logs
    return [...logs].sort((a, b) => {
      let aVal, bVal
      if (sortField === "action") {
        aVal = describe(a).label || ""
        bVal = describe(b).label || ""
      } else if (sortField === "actor") {
        aVal = a.actor?.name || ""
        bVal = b.actor?.name || ""
      } else if (sortField === "when") {
        aVal = a.occurred_at || ""
        bVal = b.occurred_at || ""
      }

      if (typeof aVal === "string") {
        return sortDirection === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal)
      } else {
        if (aVal < bVal) return sortDirection === "asc" ? -1 : 1
        if (aVal > bVal) return sortDirection === "asc" ? 1 : -1
        return 0
      }
    })
  }, [logs, sortField, sortDirection])

  const renderSortableHeader = (label, field, className = "") => {
    const isSorted = sortField === field
    return (
      <button
        onClick={() => handleSort(field)}
        className={`inline-flex items-center gap-1 hover:text-black focus:outline-none font-semibold text-black ${className}`}
      >
        <span>{label}</span>
        {isSorted ? (
          sortDirection === "asc" ? (
            <ArrowUp className="h-3.5 w-3.5 shrink-0" />
          ) : (
            <ArrowDown className="h-3.5 w-3.5 shrink-0" />
          )
        ) : (
          <ArrowUpDown className="h-3.5 w-3.5 text-slate-400 opacity-60 hover:opacity-100 shrink-0" />
        )}
      </button>
    )
  }

  const meta = data?.meta
  const startRow = ((page - 1) * (meta?.per_page || 25)) + 1
  const endRow = Math.min(startRow + logs.length - 1, meta?.total || 0)

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-slate-900">Audit Log</h2>
        <p className="text-[13px] text-slate-500">
          {meta?.total != null ? `${meta.total} recorded event${meta.total !== 1 ? "s" : ""}` : "A record of important actions in your organization"}
        </p>
      </div>

      <div className="relative max-w-md">
        <Search
          style={{ left: "0.75rem" }}
          className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
        />
        <Input
          style={{ paddingLeft: "2.5rem" }}
          placeholder="Search by action or person"
          value={searchRaw}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="w-[50px]">#</TableHead>
              <TableHead className="min-w-[320px] text-black">{renderSortableHeader("Action", "action")}</TableHead>
              <TableHead className="w-[220px] text-black">{renderSortableHeader("Performed by", "actor")}</TableHead>
              <TableHead className="w-[130px] text-right text-black">{renderSortableHeader("When", "when", "w-full justify-end")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && logs.length === 0 ? (
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={i}>
                  {[40, 280, 160, 80].map((w, j) => (
                    <TableCell key={j}><Skeleton className="h-4 rounded" style={{ width: w }} /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <ScrollText className="mb-3 h-10 w-10 text-slate-300" />
                    <p className="text-sm font-medium text-slate-600">No activity yet</p>
                    <p className="mt-1 text-xs text-slate-400">Actions like role changes and invitations will appear here.</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              sortedLogs.map((log, idx) => {
                const { label, Icon } = describe(log)
                const detail = detailLine(log)
                const expandable = isExpandable(log)
                const isExpanded = expandable && expandedId === log.id
                return (
                  <React.Fragment key={log.id}>
                    <TableRow
                      onClick={expandable ? () => toggleRow(log.id) : undefined}
                      aria-expanded={expandable ? isExpanded : undefined}
                      className={`${expandable ? "cursor-pointer" : ""} ${isExpanded ? "bg-slate-50" : ""}`}
                    >
                      <TableCell className="text-xs text-slate-400">
                        <span className="flex items-center gap-1.5">
                          <ChevronRight
                            className={`h-3.5 w-3.5 text-slate-400 transition-transform ${expandable ? "" : "invisible"} ${isExpanded ? "rotate-90" : ""}`}
                          />
                          {startRow + idx}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-start gap-2.5">
                          <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-100">
                            <Icon className="h-3.5 w-3.5 text-slate-500" />
                          </span>
                          <div className="min-w-0">
                            <p className="text-[13px] font-medium text-slate-900">{label}</p>
                            {detail && <p className="mt-0.5 truncate text-[12px] text-slate-500">{detail}</p>}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {log.actor ? (
                          <div className="flex items-center gap-2.5">
                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#ecebff] text-[10px] font-semibold text-[#4f39f6]">
                              {initials(log.actor.name)}
                            </span>
                            <div className="min-w-0">
                              <p className="truncate text-[13px] text-slate-700">{log.actor.name}</p>
                              <p className="truncate text-[11px] text-slate-400">{log.actor.email}</p>
                            </div>
                          </div>
                        ) : (
                          <span className="text-[12px] text-slate-400">System</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-[12px] text-slate-400" title={log.occurred_at}>
                        {relativeTime(log.occurred_at)}
                      </TableCell>
                    </TableRow>
                    {isExpanded && (
                      <TableRow className="bg-slate-50 hover:bg-slate-50">
                        <TableCell />
                        <TableCell colSpan={3} className="pb-4 pt-1">
                          <div className="rounded-lg border border-slate-200 bg-white p-4">
                            <AuditDetails log={log} />
                            <p className="mt-3 border-t border-slate-100 pt-2 text-[11px] text-slate-400">
                              {new Date(log.occurred_at).toLocaleString()}
                            </p>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                )
              })
            )}
          </TableBody>
        </Table>

        {!loading && logs.length > 0 && meta && (
          <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3">
            <p className="text-xs text-slate-500">
              Showing {startRow}–{endRow} of {meta.total}
            </p>
            <div className="flex items-center gap-1">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
                className="rounded-md border border-slate-200 px-2.5 py-1 text-xs text-slate-600 disabled:opacity-40 hover:bg-slate-50"
              >
                ← Prev
              </button>
              <span className="px-2 text-xs text-slate-500">{page} / {meta.total_pages || 1}</span>
              <button
                type="button"
                disabled={page >= (meta.total_pages || 1)}
                onClick={() => setPage(page + 1)}
                className="rounded-md border border-slate-200 px-2.5 py-1 text-xs text-slate-600 disabled:opacity-40 hover:bg-slate-50"
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}


